import { Component, Vue } from 'vue-property-decorator';
import template from './settings.vue';
import SettingsCompany from '@/components/settings/company';
import SettingsFeatures from '@/components/settings/features';
import SettingsLunch from '@/components/settings/lunch';
import SettingsRooms from '@/components/settings/rooms';
import SettingsCalendar from '@/components/settings/calendar';
import apiService from '@/services/api.service';
import { Action } from '@/store';
import { Company, Settings as SettingModel } from '@/definitions';
import { deepEqual } from '@/utilities';

export enum Tab {
    COMPANY = 1,
    ROOMS = 2,
    CALENDAR = 3,
    LUNCH = 4,
    FEATURES = 5
}

@Component({
    mixins: [template],
    components: {
        SettingsCompany,
        SettingsFeatures,
        SettingsRooms,
        SettingsCalendar,
        SettingsLunch
    },
})
export default class Settings extends Vue {
    activeTab: Tab = Tab.COMPANY;
    changes: Tab[] = [];
    initialModel: SettingModel | null = null;
    currentModel: SettingModel | null = null;

    async mounted() {
        try {
            await apiService.getCompany();
            this.setupModel();
        } catch (e) {
            this.$Store.dispatch(Action.SHOW_ALERT, { text: 'alerts.settings.retrieve_error', type: 'danger' });
        }
    }

    get company(): Company {
        return this.$Store.state.company.company;
    }

    get collectLunchInfo(): boolean {
        if (!this.initialModel) return false;
        return Boolean(this.initialModel.features.permissions.find(p => p.name === "COLLECT_LUNCH_INFO" && p.isEnabled));
    }

    get tabs(): { text: string; icon: string; tab: Tab }[] {
        return [
            { text: "company", icon: "fas fa-building", tab: Tab.COMPANY },
            { text: "rooms", icon: "fas fa-chair", tab: Tab.ROOMS },
            { text: "calendar", icon: "fas fa-calendar", tab: Tab.CALENDAR },
            ...(this.collectLunchInfo ? [{ text: "lunch", icon: "fas fa-utensils", tab: Tab.LUNCH }] : []),
            { text: "features", icon: "fas fa-list-ul", tab: Tab.FEATURES },
        ]
    }

    applyChange(tab: Tab, model: any) {
        if (this.initialModel && this.currentModel) {
            switch (tab) {
                case Tab.COMPANY:
                    this.currentModel.company = model;
                    this.checkForChanges(this.currentModel.company, this.initialModel.company, tab);
                    break;
                case Tab.CALENDAR:
                    this.currentModel.calendar = model;
                    this.checkForChanges(this.currentModel.calendar, this.initialModel.calendar, tab);
                    break;
                case Tab.FEATURES:
                    this.currentModel.features = model;
                    this.checkForChanges(this.currentModel.features, this.initialModel.features, tab);
                    break;
                case Tab.ROOMS:
                    this.currentModel.rooms = model;
                    this.checkForChanges(this.currentModel.rooms, this.initialModel.rooms, tab);
                    break;
            }
        }
    }

    changesSaved(tab: Tab) {
        if (this.currentModel && this.initialModel) {
            switch (tab) {
                case Tab.COMPANY:
                    this.initialModel.company = JSON.parse(JSON.stringify(this.currentModel.company));
                    break;
                case Tab.CALENDAR:
                    this.initialModel.calendar = JSON.parse(JSON.stringify(this.currentModel.calendar));
                    break;
                case Tab.FEATURES:
                    this.initialModel.features = JSON.parse(JSON.stringify(this.currentModel.features));
                    break;
                case Tab.ROOMS:
                    this.initialModel.rooms = JSON.parse(JSON.stringify(this.currentModel.rooms));
                    break;
            }
            this.changes.splice(this.changes.indexOf(tab), 1);
        }
    }

    checkForChanges(newModel: any, oldModel: any, tab: Tab) {
        if (deepEqual(newModel, oldModel) && this.changes.includes(tab)) {
            this.changes.splice(this.changes.indexOf(tab), 1);
        } else if (!deepEqual(newModel, oldModel) && !this.changes.includes(tab)) {
            this.changes.push(tab);
        }
    }

    setupModel() {
        this.initialModel = {
            calendar: JSON.parse(JSON.stringify(this.company.calendar)),
            company: { name: this.company.name },
            features: { permissions: this.company.permissions },
            rooms: JSON.parse(JSON.stringify(this.$Store.state.company.rooms))
        };
        this.currentModel = JSON.parse(JSON.stringify(this.initialModel));
    }
}